import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldAlert, RefreshCw, FileText, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function CoachImportPreflight() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState({});
  const [openBatch, setOpenBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/historical-imports/preflight-summary`);
      setSummary(data);
    } catch (e) {
      toast.error("Failed to load: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openBatchDetail = async (batchId) => {
    if (batches[batchId]) {
      setOpenBatch(batchId === openBatch ? null : batchId);
      return;
    }
    try {
      const { data } = await api.get(`/historical-imports/batches/${batchId}/preflight`);
      setBatches({ ...batches, [batchId]: data });
      setOpenBatch(batchId);
    } catch (e) {
      toast.error("Failed to load batch: " + (e?.response?.data?.detail || e.message));
    }
  };

  const totals = summary?.totals || {};
  const list = summary?.batches || [];
  const hasAnyReady = (totals.ready_docs || 0) > 0;

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="coach-import-preflight">
      <button onClick={() => nav("/app/coach")} data-testid="preflight-back"
              className="flex items-center gap-1 text-sm text-zinc-500 hover:text-[#FF5A00] mb-3">
        <ArrowLeft className="w-4 h-4"/> Back to Coach
      </button>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            Import Preflight (read-only)
          </div>
          <h1 className="font-heading text-3xl font-bold">Historical invoices sitting in Ready</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Read-only inspection of every batch still pending your explicit approval. No writes, no side effects — running this page never imports anything.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="preflight-refresh">
          <RefreshCw className="w-3 h-3 mr-1"/> Refresh
        </Button>
      </div>

      <Card className="p-4 mb-6 bg-amber-50 border-amber-200 flex items-start gap-3" data-testid="preflight-safety">
        <ShieldAlert className="w-5 h-5 text-amber-700 mt-0.5"/>
        <div className="text-sm text-amber-800">
          <b>Safety:</b> This page is read-only. To actually import a batch, open Data Import → click <span className="font-mono">Import ready</span> on the batch. Every import is idempotent — already-imported docs are skipped.
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Kpi testid="preflight-kpi-ready" label="Ready docs" value={totals.ready_docs ?? "—"}/>
        <Kpi testid="preflight-kpi-review" label="Needs review" value={totals.needs_review_docs ?? "—"}/>
        <Kpi testid="preflight-kpi-dupes" label="Duplicates" value={totals.duplicate_docs ?? "—"}/>
        <Kpi testid="preflight-kpi-amount" label="Expected total" value={`$${(totals.expected_amount ?? 0).toLocaleString()}`}/>
      </div>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}
      {!loading && !hasAnyReady && (
        <Card className="p-6 text-center text-zinc-500" data-testid="preflight-empty">
          No batches with ready documents. Nothing pending import.
        </Card>
      )}

      <div className="space-y-3">
        {list.map((b) => (
          <Card key={b.id} className="p-4" data-testid={`preflight-batch-${b.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-zinc-500"/>
                  {b.label || "Untitled batch"}
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{b.status}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {b.ready_count} ready · {b.duplicate_count} duplicates · {b.needs_review_count} needs review · ${b.expected_amount.toLocaleString()} expected
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => openBatchDetail(b.id)}
                      data-testid={`preflight-inspect-${b.id}`}>
                {openBatch === b.id ? "Hide" : "Inspect"}
              </Button>
            </div>
            {openBatch === b.id && batches[b.id] && (
              <div className="mt-3 border-t border-zinc-200 pt-3" data-testid={`preflight-detail-${b.id}`}>
                <div className="text-xs text-zinc-600 mb-2">
                  {batches[b.id].idempotency_note}
                </div>
                {batches[b.id].warnings_seen?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs mb-2 flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 text-amber-700 mt-0.5"/>
                    <div>
                      <b>Warnings observed:</b> {batches[b.id].warnings_seen.join(", ")}
                    </div>
                  </div>
                )}
                <table className="w-full text-xs" data-testid={`preflight-files-${b.id}`}>
                  <thead className="text-zinc-500 uppercase">
                    <tr>
                      <th className="text-left px-2 py-1">File</th>
                      <th className="text-left px-2 py-1">Invoice #</th>
                      <th className="text-left px-2 py-1">Customer</th>
                      <th className="text-left px-2 py-1">Total</th>
                      <th className="text-left px-2 py-1">Status hint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches[b.id].files.map((f) => (
                      <tr key={f.doc_id} className="border-t border-zinc-100">
                        <td className="px-2 py-1">{f.filename}</td>
                        <td className="px-2 py-1 font-mono">{f.invoice_number || "—"}</td>
                        <td className="px-2 py-1">{f.customer_name || "—"}</td>
                        <td className="px-2 py-1 text-right">${(f.total || 0).toFixed(2)}</td>
                        <td className="px-2 py-1"><span className="font-mono text-[10px]">{f.status_hint}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3"/>
                  Next step: {batches[b.id].next_step}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono-accent">{label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
